<template>
  <div class="navbar-container d-flex content align-items-center">

    <!-- Nav Menu Toggler -->
    <ul class="nav navbar-nav d-xl-none">
      <li class="nav-item">
        <b-link
          class="nav-link"
          @click="toggleVerticalMenuActive"
        >
          <feather-icon
            icon="MenuIcon"
            size="21"
          />
        </b-link>
      </li>
    </ul>

    <!-- Left Col -->
    <div class="bookmark-wrapper align-items-center flex-grow-1 d-lg-flex">
      <h4
        v-if="showTitle"
        style="margin-top:6px;margin-left:6px;"
      >
        Hot Alerts
      </h4>
      <b-link
        v-if="showGlobalFilterLink"
        @click="showGlobalFilter"
      >
        {{ globalFilter.fromWave }} - {{ globalFilter.toWave }}
        <span
          v-if="globalFilter.rolling && globalFilter.rolling > 1"
          class="ml-1"
        >{{ $t('Roll') }}: {{
          globalFilter.rolling
        }}</span>
        <span
          v-if="filterDisplay"
          class="ml-1"
          :title="filterDisplay"
        >{{ $t('Filtered') }}</span>
      </b-link>
    </div>

    <b-navbar-nav class="nav align-items-center ml-auto">
      <locale class="d-sm-flex d-none" />
      <dark-Toggler class="d-none d-lg-block" />
      <message-dropdown
        class="d-sm-flex d-none"
        :user-level="userLevel"
      />
      <library-dropdown
        class="d-sm-flex d-none"
        :user-level="userLevel"
      />
      <legal-info-link class="d-sm-flex d-none" />
      <faq-link class="d-sm-flex d-none" />
      <hot-alert-link class="d-sm-flex d-none" />
      <approval-link class="d-sm-flex d-none" />
      <appeal-link class="d-sm-flex d-none" />
      <request-link class="d-sm-flex d-none" />
      <user-dropdown />
      <modal-global-filter @filterApplied="filterApplied" />
      <modal-item
        item-type="page"
        :parent-id="newMenuItemId"
        :modal-name="'-newMenuItem'"
        @submit="itemAdded"
      />

      <modal-message />
      <modal-library />

    </b-navbar-nav>
  </div>
</template>

<script>
import {
  BLink, BNavbarNav,
} from 'bootstrap-vue'
import { initialAbility } from '@/libs/acl/config'
// import DarkToggler from '@core/layouts/components/app-navbar/components/DarkToggler.vue'
import useJwt from '@/auth/jwt/useJwt'
import axios from 'axios'

import { getUserData, getUserLevel } from '@/auth/utils'
import {
  periodList,
  hotAlertCount,
  approvalCount,
  appealCount,
} from '@core/layouts/utils'
import ModalGlobalFilter from './ModalGlobalFilter.vue'
import ModalItem from './ModalItem.vue'

import Locale from './Locale.vue'
import DarkToggler from './DarkToggler.vue'
import MessageDropdown from './MessageDropdown.vue'
import LibraryDropdown from './LibraryDropdown.vue'
import HotAlertLink from './HotAlertLink.vue'
import ApprovalLink from './ApprovalLink.vue'
import AppealLink from './AppealLink.vue'
import RequestLink from './RequestLink.vue'
import LegalInfoLink from './LegalInfoLink.vue'
import UserDropdown from './UserDropdown.vue'
import ModalMessage from './ModalMessage.vue'
import ModalLibrary from './ModalLibrary.vue'
import FaqLink from './FaqLink.vue'

export default {
  components: {
    BLink,
    BNavbarNav,

    // Navbar Components
    DarkToggler,
    Locale,
    MessageDropdown,
    LibraryDropdown,
    HotAlertLink,
    ApprovalLink,
    AppealLink,
    RequestLink,
    LegalInfoLink,
    UserDropdown,
    ModalGlobalFilter,
    ModalItem,
    ModalMessage,
    ModalLibrary,
    FaqLink,
  },
  props: {
    toggleVerticalMenuActive: {
      type: Function,
      default: () => { },
    },
  },
  data() {
    return {
      userData: null,
      userLevel: 0,
      globalFilter: null,
      newMenuItemId: null,
      hotAlertCount: null,
      filterDisplay: null,
    }
  },
  computed: {
    showGlobalFilterLink() {
      return this.$route && this.$route.name && this.$route.name === 'dashboard' && this.globalFilter
    },
    showTitle() {
      return this.$route && this.$route.name && this.$route.name === 'hotalert'
    },
  },
  async created() {
    this.userData = getUserData()
    this.userLevel = this.userData ? getUserLevel(this.userData.role) : 0
    await periodList()
    await hotAlertCount()
    this.$root.$emit('hotalert_count', this.$store.state.globals.hotalertCount)

    await approvalCount()
    this.$root.$emit('approval_count', this.$store.state.globals.approvalCount)

    await appealCount()
    this.$root.$emit('appeal_count', this.$store.state.globals.appealCount)

    this.globalFilter = this.$store.state.globals.globalFilter
    this.setFilterDisplay()
  },
  mounted() {
    this.$root.$on('addNewMenuItem', item => {
      this.newMenuItemId = item.id
      this.$bvModal.show('add-item-newMenuItem')
    })
  },
  beforeDestroy() {
    this.$root.$off('addNewMenuItem')
  },
  methods: {
    filterApplied() {
      this.globalFilter = this.$store.state.globals.globalFilter
      this.setFilterDisplay()
      this.$root.$emit('filterApplied')
    },
    setFilterDisplay() {
      this.filterDisplay = ''
      const filters = []
      if (this.globalFilter.filterConfig.filters && this.globalFilter.filterConfig.filters.length > 0) {
        this.globalFilter.filterConfig.filters.forEach(f => {
          if (f.operator) {
            filters.push(` ${f.operator} `)
          } else if (f.filterValues && f.filterValues.length) {
            const values = f.filterValues.map(x => x.optionText).join(',')
            filters.push(`[${f.filterQuestion.questionText} = ${values}]`)
          } else if (f.filterValuesText && f.filterValuesText.length) {
            const values = f.filterValuesText.map(x => x).join(',')
            filters.push(`[${f.filterQuestion.questionText} = ${values}]`)
          }
        })
        this.filterDisplay = filters.join('')
      }
    },
    showGlobalFilter() {
      this.$bvModal.show('modal-global-filter')
    },
    itemAdded() {
      this.$bvModal.hide('add-item-newMenuItem')
      this.$root.$emit('refreshMenu')
    },
    logout() {
      useJwt.axiosIns.get('settings/SsoLogoutUrl')
        .then(response => {
          if (response.data) {
            const ssoLogoutUrl = response.data

            axios.post(ssoLogoutUrl, {})
              .then(() => {
                // Remove userData from localStorage
                // ? You just removed token from localStorage. If you like, you can also make API call to backend to blacklist used token
                localStorage.removeItem(useJwt.jwtConfig.storageTokenKeyName)
                localStorage.removeItem(useJwt.jwtConfig.storageRefreshTokenKeyName)

                // Remove userData from localStorage
                localStorage.removeItem('userData')

                // Reset ability
                this.$ability.update(initialAbility)

                // Redirect to loggedOut page
                this.$router.push({ name: 'loggedOut' })
              })
              .catch(error => {
                console.log(error)
              })
          } else {
            // Remove userData from localStorage
            // ? You just removed token from localStorage. If you like, you can also make API call to backend to blacklist used token
            localStorage.removeItem(useJwt.jwtConfig.storageTokenKeyName)
            localStorage.removeItem(useJwt.jwtConfig.storageRefreshTokenKeyName)

            // Remove userData from localStorage
            localStorage.removeItem('userData')

            // Reset ability
            this.$ability.update(initialAbility)

            // Redirect to loggedOut page
            this.$router.push({ name: 'loggedOut' })
          }
        })
        .catch(e => {
          console.log(e)
        })
    },
  },
}
</script>
